import { getAxiosInstance } from '../../react/utils/networkUtil';
import { PaginationQuery, PaginationResult } from '../models/AppPagination';
import { AppSuccess } from '../models/AppSuccess';
import {
  UpdateProject_Response,
  UpdateProject_Request,
  CreateProject_Request,
  Project_Base,
  CreateProject_Response,
} from '../models/Project';
import { StrapiImage } from '../models/StrapiImage';
import { User_Base } from '../models/User';
import {
  AC_Project_addRoleForUser_Request,
  AC_Project_addRoleForUser_Response,
  AC_Project_Base,
  AC_Project_deleteRoleForUser_Request_Query,
  AC_Project_deleteRoleForUser_Response,
  AC_Project_getUsers_with_Roles_Request_Query,
  AC_Project_getUsers_with_Roles_Response,
  AC_Project_Role,
} from '../services/access_control/AC_Project';

export type Project_getProjects_Request = PaginationQuery & {
  phone_number?: string;
  project_name?: string;
  id?: number;
  organizationDocumentId?: string;
};
export const remote_getProjects = async (
  request: Project_getProjects_Request,
): Promise<PaginationResult<Project_Base>> => {
  return (
    await getAxiosInstance().get('/api/projects-ext', {
      params: request,
    })
  ).data;
};

export const remote_getMyProjects = async (): Promise<Array<Project_Base>> => {
  try {
    const response = await getAxiosInstance().get('/api/projects-ext/my-projects');
    return response.data;
  } catch (e) {
    console.log('ERROR: when get my projects');
    throw e;
  }
};

export const remote_getProjectsSharedWithMe = async (): Promise<Array<AC_Project_Base>> => {
  try {
    const response = await getAxiosInstance().get('/api/projects-ext/shared-with-me');
    return response.data;
  } catch (e) {
    console.log('ERROR: when get shared projects');
    throw e;
  }
};

export const remote_deleteProject = async (projectId: number): Promise<AppSuccess<Project_Base>> => {
  return (await getAxiosInstance().delete(`/api/projects-ext/${projectId}`)).data;
};

export const remote_rollbackProject = async (projectId: number): Promise<AppSuccess<Project_Base>> => {
  return (await getAxiosInstance().put(`/api/projects-ext/rollback/${projectId}`)).data;
};

export const remote_getProjectsByWorkspaceId = async (workspaceId: number): Promise<Array<Project_Base>> => {
  try {
    const response = await getAxiosInstance().get('/api/projects-ext/in-workspace?workspaceId=' + workspaceId);
    return response.data;
  } catch (e) {
    console.log('ERROR: when get projects by workspace id');
    throw e;
  }
};

export type Project_MemberItem = {
  user: User_Base;
  role: AC_Project_Role;
};
export type Project_GetById_Response = Project_Base & {
  members: Array<Project_MemberItem>;
};
/**
 * Get project by numeric ID
 * @deprecated Use remote_getProjectByDocumentId instead for better performance with documentId
 */
export const remote_getProjectById = async (projectId: number | string): Promise<Project_GetById_Response> => {
  const response = await getAxiosInstance().get(`/api/projects-ext/get-one/${projectId}`);
  return response.data;
};

export const remote_getProjectByDocumentId = async (documentId: string): Promise<Project_GetById_Response> => {
  const response = await getAxiosInstance().get(`/api/projects-ext/document-id/${documentId}`);
  return response.data;
};

export const remote_createProject = async (body: CreateProject_Request): Promise<CreateProject_Response> => {
  const response = await getAxiosInstance().post('/api/projects-ext/', body);
  const createdProjectResponse: CreateProject_Response = response.data;
  return createdProjectResponse;
};

export const remote_updateProject = async (
  project: UpdateProject_Request,
  projectId: number | string,
): Promise<UpdateProject_Response> => {
  const formData = new FormData();
  if (project.image) {
    formData.append('image', project.image);
  }
  if (project.avatar) {
    formData.append('avatar', project.avatar);
  }
  if (project.name) {
    formData.append('name', project.name!);
  }
  if (project.description) {
    formData.append('description', project.description!);
  }
  if (project.boundingBox) {
    formData.append('boundingBox', project.boundingBox!);
  }
  if (project.homepage) {
    formData.append('homepage', project.homepage!);
  }
  if (project.portal_api_base_url) {
    formData.append('portal_api_base_url', project.portal_api_base_url!);
  }
  if (project.isPublished !== null && project.isPublished !== undefined) {
    formData.append('isPublished', project.isPublished ? 'true' : 'false');
  }
  // organizationId: >0 = connect to organization, 0 = disconnect/remove organization
  if (project.organizationId !== undefined) {
    formData.append('organizationId', String(project.organizationId));
  }

  const response = await getAxiosInstance().put(`/api/projects-ext/${projectId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const remote_uploadFile = async (file: File): Promise<Array<StrapiImage>> => {
  const formData = new FormData();
  formData.append('files', file);
  const response = await getAxiosInstance({
    noAuth: true,
  }).post('/api/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const remote_getUsers_with_Roles = async (
  query: AC_Project_getUsers_with_Roles_Request_Query,
): Promise<AC_Project_getUsers_with_Roles_Response> => {
  const response = await getAxiosInstance().get('/api/acl-project-ext/users-with-roles', {
    params: query,
  });
  return response.data;
};

export const remote_addRoleForUser = async (
  data: AC_Project_addRoleForUser_Request,
): Promise<AC_Project_addRoleForUser_Response> => {
  const response = await getAxiosInstance().post('/api/acl-project-ext/roles', {
    data: data,
  });
  return response.data;
};

export const remote_deleteRoleForUser = async (
  query: AC_Project_deleteRoleForUser_Request_Query,
): Promise<AC_Project_deleteRoleForUser_Response> => {
  const response = await getAxiosInstance().delete('/api/acl-project-ext/roles', {
    params: query,
  });
  return response.data;
};

export const remote_removeMemberFromProject = async (
  projectId: number,
  userId: number,
): Promise<AppSuccess<undefined>> => {
  return (await getAxiosInstance().delete(`/api/projects-ext/remove-member/${projectId}/${userId}`)).data;
};

export const ProjectAPIs = {
  remote_getProjects,
  remote_getMyProjects,
  remote_getProjectsSharedWithMe,
  remote_getProjectsByWorkspaceId,
  remote_getProjectById,
  remote_getProjectByDocumentId,
  remote_createProject,
  remote_updateProject,
  remote_deleteProject,
  remote_rollbackProject,
  remote_uploadFile,
  remote_getUsers_with_Roles,
  remote_addRoleForUser,
  remote_deleteRoleForUser,
  remote_removeMemberFromProject,
};
